import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import {
  LayoutDashboard,
  Search,
  FileSpreadsheet,
  ClipboardCheck,
  ClipboardList,
  Kanban,
  Sparkles,
  BarChart3,
  Settings,
  LogOut,
  User,
  ChevronDown,
  Bell,
  Calendar,
  Sun,
  Moon,
  HelpCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const commonItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/parts', label: 'Part Search', icon: Search },
  { path: '/templates', label: 'Cost Templates', icon: FileSpreadsheet },
  { path: '/cogs-cards', label: 'COGS Cards', icon: ClipboardCheck },
  // { path: '/my-tasks', label: 'My Tasks', icon: ClipboardList },
  // { path: '/kanban', label: 'Kanban Board', icon: Kanban },
];

const adminItems = [
  { path: '/admin', label: 'Admin Panel', icon: Settings },
  { path: '/help', label: 'Help & Support', icon: HelpCircle },
  { path: '/insights', label: 'AI Insights', icon: Sparkles },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
];

const creatorItems = [
];

const approverItems = [];


export const Sidebar = () => {
  const { user, logout, hasRole } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-[#C3002F]/20 text-[#C3002F] border-[#C3002F]/30';
      case 'approver': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'creator': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      default: return 'bg-zinc-800 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-400';
    }
  };

  return (
    <aside className="w-64 h-screen bg-white dark:bg-zinc-950 border-r border-zinc-200 dark:border-zinc-800 flex flex-col transition-colors duration-300" data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded bg-[#C3002F] flex items-center justify-center">
            <span className="text-white font-bold">N</span>
          </div>
          <div>
            <span className="text-lg font-bold text-zinc-900 dark:text-white tracking-tight">Pricing Tool</span>
            <p className="text-xs text-zinc-500">Enterprise Edition</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <p className="text-xs uppercase text-zinc-500 font-medium tracking-wider px-3 mb-3">Main Menu</p>
        {commonItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.path.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${isActive
                ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white'
                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-50 dark:hover:bg-zinc-900'
              }`
            }
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </NavLink>
        ))}

        {hasRole('admin') && (
          <>
            <div className="pt-4 pb-2">
              <p className="text-xs uppercase text-zinc-500 font-medium tracking-wider px-3 mb-3">Administration</p>
            </div>
            {adminItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${isActive
                    ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white'
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-50 dark:hover:bg-zinc-900'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </>
        )}

        {hasRole('creator') && creatorItems.length > 0 && (
          <>
            <div className="pt-4 pb-2">
              <p className="text-xs uppercase text-zinc-500 font-medium tracking-wider px-3 mb-3">Creator</p>
            </div>
            {creatorItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${isActive
                    ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white'
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-50 dark:hover:bg-zinc-900'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </>
        )}

        {hasRole('approver') && approverItems.length > 0 && (
          <>
            <div className="pt-4 pb-2">
              <p className="text-xs uppercase text-zinc-500 font-medium tracking-wider px-3 mb-3">Approver</p>
            </div>
            {approverItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${isActive
                    ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white'
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-50 dark:hover:bg-zinc-900'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </>
        )}

      </nav>

      {/* Theme Toggle & User section */}
      <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 space-y-3">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleTheme}
          className="w-full justify-start gap-3 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
          data-testid="theme-toggle"
        >
          {isDark ? (
            <>
              <Sun className="w-4 h-4" />
              <span>Light Mode</span>
            </>
          ) : (
            <>
              <Moon className="w-4 h-4" />
              <span>Dark Mode</span>
            </>
          )}
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors" data-testid="user-menu-btn">
              <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center">
                <User className="w-4 h-4 text-zinc-600 dark:text-zinc-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-zinc-900 dark:text-white truncate">{user?.name}</p>
                <span className={`inline-flex text-xs px-1.5 py-0.5 rounded border ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-zinc-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
            <DropdownMenuItem className="text-zinc-700 dark:text-zinc-300">
              <User className="w-4 h-4 mr-2" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-zinc-200 dark:bg-zinc-800" />
            <DropdownMenuItem onClick={handleLogout} className="text-red-500 dark:text-red-400" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};

export const MainLayout = ({ children }) => {
  return (
    <div className="flex h-screen bg-zinc-100 dark:bg-[#09090b] transition-colors duration-300" data-testid="main-layout">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="grid-bg min-h-full">
          {children}
        </div>
      </main>
    </div>
  );
};
